import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare, Send, AlertCircle, User, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { EmailSelector } from '@/components/ui/EmailSelector';
import { MentionDropdown } from '@/components/ui/MentionDropdown';
import { MentionText } from '@/components/ui/MentionText';
import { useMentions } from '@/hooks/useMentions';
import type { Tables } from '@/integrations/supabase/types';

interface CommentFormProps {
  reportId: string;
  reportName?: string;
  reportOwnerName?: string;
  reportOwnerEmail?: string;
}

type Comment = Tables<'comments'>;

export const CommentForm: React.FC<CommentFormProps> = ({ reportId, reportName, reportOwnerName, reportOwnerEmail }) => {
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendingComments, setResendingComments] = useState<Set<string>>(new Set());
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Refs for mention functionality
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number>(0);
  
  // Mention functionality
  const {
    mentionState,
    filteredUsers,
    selectedIndex,
    isLoading: mentionsLoading,
    handleTextChange,
    handleKeyDown: handleMentionKeyDown,
    selectUser,
    setSelectedIndex,
    closeMentions,
  } = useMentions(comment, setComment, (email: string) => {
    // Auto-add mentioned email to To list
    if (!toEmails.includes(email)) {
      setToEmails(prev => [...prev, email]);
      // Auto-show advanced section if mention was used
      if (!showAdvanced) {
        setShowAdvanced(true);
      }
    }
  });

  // Load existing comments
  useEffect(() => {
    loadComments();
  }, [reportId]);

  // Handle click outside to close mentions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (textareaRef.current && !textareaRef.current.contains(event.target as Node)) {
        closeMentions();
      }
    };

    if (mentionState.isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mentionState.isActive, closeMentions]);

  // Send email notification via edge function
  const sendEmailNotification = async (commentData: Comment) => {
    try {
      // Parse to_emails and cc_emails from the comment data
      const toEmailsList = commentData.to_emails ? commentData.to_emails.split(',').map(e => e.trim()).filter(e => e) : [];
      const ccEmailsList = commentData.cc_emails ? commentData.cc_emails.split(',').map(e => e.trim()).filter(e => e) : [];
      
      // Always include report owner email in To unless it's already in To or CC
      const allEmailsSet = new Set([...toEmailsList, ...ccEmailsList]);
      if (reportOwnerEmail && !allEmailsSet.has(reportOwnerEmail)) {
        toEmailsList.unshift(reportOwnerEmail);
      }

      const response = await supabase.functions.invoke('send-comment-email', {
        body: {
          reportId,
          reportName: reportName || 'Unknown Report',
          commentText: commentData.comment_text,
          commenterName: commentData.user_name || 'Anonymous',
          commenterEmail: commentData.user_email || '',
          reportOwnerName: reportOwnerName || 'Unknown User',
          reportOwnerEmail: reportOwnerEmail || '',
          toEmails: toEmailsList,
          ccEmails: ccEmailsList
        }
      });

      if (response.error) {
        console.error('Error sending email notification:', response.error);
        throw new Error('Không thể gửi email thông báo');
      } else {
        console.log('Email notification sent successfully:', response.data);
        return true;
      }
    } catch (error) {
      console.error('Failed to send email notification:', error);
      throw error;
    }
  };

  // Resend email notification for a specific comment
  const handleResendEmail = async (commentData: Comment) => {
    if (!reportOwnerEmail) {
      setError('Không có email người nhận để gửi nhắc nhở');
      return;
    }

    setResendingComments(prev => new Set(prev).add(commentData.id));
    setError(null);

    try {
      await sendEmailNotification(commentData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error resending email:', error);
      setError('Không thể gửi lại email nhắc nhở. Vui lòng thử lại.');
    } finally {
      setResendingComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentData.id);
        return newSet;
      });
    }
  };

  const loadComments = async () => {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('report_id', reportId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setComments(data || []);
    } catch (err) {
      console.error('Error loading comments:', err);
      setError('Không thể tải comments');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      setError('Vui lòng nhập nội dung comment');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          report_id: reportId,
          comment_text: comment.trim(),
          user_name: user?.user_metadata?.full_name || user?.email || 'Anonymous',
          user_email: user?.email || null,
          to_emails: toEmails.length > 0 ? toEmails.join(',') : null,
          cc_emails: ccEmails.length > 0 ? ccEmails.join(',') : null
        })
        .select()
        .single();

      if (error) throw error;

      // Add new comment to the list
      setComments([data, ...comments]);
      setComment('');
      setToEmails([]);
      setCcEmails([]);
      setShowAdvanced(false);
      setSuccess(true);
      // Close mentions if active
      closeMentions();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

      // Send email notification
      if (reportName && reportOwnerEmail) {
        await sendEmailNotification(data);
      }

    } catch (err) {
      console.error('Error saving comment:', err);
      setError('Không thể lưu comment. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-gray-600" />
        <h5 className="font-medium text-gray-900 text-sm">Comments</h5>
      </div>

      {/* Existing comments */}
      {comments.length > 0 && (
        <div className="mb-4 space-y-2 max-h-60 overflow-y-auto">
          {comments.map((commentItem) => (
            <div key={commentItem.id} className="bg-gray-50 rounded-md p-3">
              <div className="flex items-start gap-2 mb-2">
                <User className="h-3 w-3 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-gray-700">
                      {commentItem.user_name || 'Anonymous'}
                    </span>
                    {commentItem.user_email && (
                      <>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500 truncate">
                          {commentItem.user_email}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-900 mb-2 ml-5">
                <MentionText text={commentItem.comment_text} />
              </div>
              <div className="flex items-center justify-between ml-5">
                <p className="text-xs text-gray-500">
                  {new Date(commentItem.created_at).toLocaleString('vi-VN')}
                </p>
                {reportOwnerEmail && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResendEmail(commentItem)}
                    disabled={resendingComments.has(commentItem.id)}
                    className="text-xs h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${resendingComments.has(commentItem.id) ? 'animate-spin' : ''}`} />
                    {resendingComments.has(commentItem.id) ? 'Đang gửi...' : 'Gửi lại'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comment form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {!user && (
          <Alert className="py-2 border-amber-200 bg-amber-50">
            <AlertDescription className="text-sm text-amber-800">
              Bạn chưa đăng nhập. Comment sẽ được lưu dưới tên "Anonymous".
            </AlertDescription>
          </Alert>
        )}
        
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={comment}
            onChange={(e) => {
              const newValue = e.target.value;
              const cursorPosition = e.target.selectionStart;
              cursorPositionRef.current = cursorPosition;
              setComment(newValue);
              handleTextChange(newValue, cursorPosition);
            }}
            onKeyDown={(e) => {
              // Handle mention navigation first
              if (handleMentionKeyDown(e)) {
                return; // Mention system handled the event
              }
              // Let default behavior continue for other keys
            }}
            onSelect={(e) => {
              // Track cursor position for mentions
              const target = e.target as HTMLTextAreaElement;
              cursorPositionRef.current = target.selectionStart;
            }}
            onClick={(e) => {
              // Track cursor position on click
              const target = e.target as HTMLTextAreaElement;
              cursorPositionRef.current = target.selectionStart;
              handleTextChange(comment, target.selectionStart);
            }}
            placeholder="Nhập comment của bạn về Summary AI này... (Gõ @ để mention người dùng)"
            className="min-h-[80px] text-sm resize-none"
            disabled={isSubmitting}
          />
          
          {/* Mention Dropdown */}
          {mentionState.isActive && (
            <MentionDropdown
              users={filteredUsers}
              selectedIndex={selectedIndex}
              onSelectUser={selectUser}
              onSetSelectedIndex={setSelectedIndex}
              isLoading={mentionsLoading}
              searchText={mentionState.searchText}
            />
          )}
        </div>

        {/* Comment preview with styled mentions */}
        {comment.trim() && comment.includes('@') && (
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
            <div className="text-xs text-gray-600 mb-2 font-medium">Preview:</div>
            <div className="text-sm">
              <MentionText text={comment} />
            </div>
          </div>
        )}

        {/* Mention hint */}
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <span>💡 <strong>Tip:</strong> Gõ @ để mention và tự động thêm email người dùng vào danh sách gửi</span>
          {mentionState.isActive && (
            <span className="text-blue-600 animate-pulse">
              ↑ Đang tìm kiếm users...
            </span>
          )}
        </div>

        {/* Advanced email options */}
        <div className="border-t pt-3">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mb-3"
          >
            {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            Gửi email thêm cho người khác
            {(toEmails.length > 0 || ccEmails.length > 0) && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                {toEmails.length + ccEmails.length} email
              </span>
            )}
            {toEmails.length > 0 && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full ml-1">
                {toEmails.filter(email => 
                  comment.includes(`@${email}`) || 
                  comment.includes(`@${email.split('@')[0]}`)
                ).length} từ @mention
              </span>
            )}
          </button>

          {showAdvanced && (
            <div className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-xs text-gray-600 mb-3">
                <strong>Lưu ý:</strong> Email sẽ luôn được gửi cho người làm báo cáo ({reportOwnerEmail || 'chưa xác định'}). 
                Phần này để gửi thêm cho những người khác nếu cần.
              </div>
              
              <EmailSelector
                label="To (Gửi cho)"
                placeholder="Chọn hoặc nhập email..."
                value={toEmails}
                onChange={setToEmails}
                className="mb-3"
              />
              
              <EmailSelector
                label="CC (Gửi bản sao cho)"
                placeholder="Chọn hoặc nhập email..."
                value={ccEmails}
                onChange={setCcEmails}
              />
            </div>
          )}
        </div>
        
        {error && (
          <Alert variant="destructive" className="py-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="py-2 border-green-200 bg-green-50">
            <AlertDescription className="text-sm text-green-800">
              Comment đã được lưu thành công!
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting || !comment.trim()}
            className="flex items-center gap-2"
          >
            <Send className="h-3 w-3" />
            {isSubmitting ? 'Đang gửi...' : 'Gửi Comment'}
          </Button>
        </div>
      </form>
    </div>
  );
}; 